import { useEffect, useId, useRef } from "react";

import type { AccountSummary } from "../../lib/account";
import { initialOf } from "../../lib/account";
import { formatPoints } from "../../lib/format";

export interface AccountMenuProps {
  open: boolean;
  /** `/api/me` 的摘要；加载中为 null，失败时由 `error` 兜底。 */
  account: AccountSummary | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  /** 额度余额 → 用量与配额弹层（M4 已有）。 */
  onOpenUsage: () => void;
  onLogout: () => void;
  loggingOut?: boolean;
}

/**
 * 用户设置浮层（Wiki G-05：点左下角用户 → 账号信息 / 额度余额 / 退出登录）。
 *
 * 只读 `/api/me` 已有的字段，不猜配额；退出登录走 `POST /api/auth/logout`，
 * 成功后由页面跳回 `/login`（这里只负责发动作）。
 */
export function AccountMenu({
  open,
  account,
  loading = false,
  error = null,
  onClose,
  onOpenUsage,
  onLogout,
  loggingOut = false,
}: AccountMenuProps) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    restoreRef.current = active instanceof HTMLElement ? active : null;
    const timer = window.setTimeout(() => closeRef.current?.focus(), 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      restoreRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="dp-scrim" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="dp-dialog dp-account"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dp-account-head">
          <div>
            <p className="dp-label">account · settings</p>
            <h2 id={titleId} className="dp-account-title">
              用户设置
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="dp-icon-btn"
            aria-label="关闭用户设置"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {loading && !account ? (
          <p className="dp-tiny-note">正在读取账号…</p>
        ) : error && !account ? (
          <p role="alert" className="dp-account-error">
            {error}
          </p>
        ) : account ? (
          <>
            <section className="dp-account-section" aria-label="账号信息">
              <p className="dp-account-label">账号信息</p>
              <div className="dp-account-identity">
                <span className="dp-account-avatar" aria-hidden="true">
                  {initialOf(account.name)}
                </span>
                <div className="dp-account-identity-text">
                  <b>{account.name}</b>
                  <span>{account.email}</span>
                  <span className="dp-account-id">{account.accountId}</span>
                </div>
              </div>
              <div className="dp-account-kv">
                <span>套餐</span>
                <b>{account.planName ?? (account.connected ? "未开通套餐" : "未连接计费账户")}</b>
              </div>
            </section>

            <section className="dp-account-section" aria-label="额度余额">
              <p className="dp-account-label">额度余额</p>
              <div className="dp-account-balance">
                <b>{formatPoints(account.usablePoints)}</b>
                <span>点可用</span>
              </div>
              <button
                type="button"
                className="dp-btn sm"
                onClick={onOpenUsage}
                aria-haspopup="dialog"
              >
                查看用量与配额
              </button>
            </section>

            <section className="dp-account-section is-last" aria-label="退出登录">
              <button
                type="button"
                className="dp-btn sm danger"
                onClick={onLogout}
                disabled={loggingOut}
              >
                {loggingOut ? "正在退出…" : "退出登录"}
              </button>
              <p className="dp-tiny-note">退出后回到登录页；本地话题记录会保留。</p>
            </section>
          </>
        ) : (
          <p className="dp-tiny-note">暂无账号数据。</p>
        )}
      </div>
    </div>
  );
}
